import ScrollReveal from "@/components/ScrollReveal";
import { Shield, Server, BarChart3, ArrowUpRight } from "lucide-react";

const services = [
  {
    index: "I",
    icon: Shield,
    title: "Sovereign Custody",
    lead: "Post-quantum vault architecture",
    description:
      "Multi-jurisdictional custody designed for regulated entities. Air-gapped key management with NIST-approved post-quantum cryptographic standards (CRYSTALS-Kyber, CRYSTALS-Dilithium), HSM-backed signing, and full segregation of client assets. Q-Day hardened since inception.",
    highlights: ["CRYSTALS-Kyber", "HSM-Backed", "Multi-Sig", "Q-Day Hardened"],
    metric: "$2.8B",
    metricLabel: "Assets Under Custody",
  },
  {
    index: "II",
    icon: Server,
    title: "Infrastructure Provisioning",
    lead: "Quantum-resistant data residency",
    description:
      "Enterprise bare-metal and cloud infrastructure provisioned for regulated entities. Fully managed hosting with sovereign data residency guarantees, post-quantum TLS tunnels, and 99.99% uptime SLA. All traffic encrypted with lattice-based cryptography.",
    highlights: ["PQ-TLS", "Bare-Metal", "Data Sovereignty", "99.99% SLA"],
    metric: "99.99%",
    metricLabel: "Uptime Guarantee",
  },
  {
    index: "III",
    icon: BarChart3,
    title: "Asset Intelligence",
    lead: "Quantum-aware risk analytics",
    description:
      "Our analytics platform integrates on-chain provenance, regulatory risk exposure, quantum vulnerability assessment, and liquidity depth. Purpose-built for CFOs, compliance officers, and board-level reporting in the post-quantum era.",
    highlights: ["PQC Scoring", "Real-Time", "Compliance Ready", "API Access"],
    metric: "140+",
    metricLabel: "Regulatory Frameworks",
  },
];

export default function InstitutionalOverview() {
  return (
    <section
      id="overview"
      data-testid="overview-section"
      className="py-28 md:py-36 lg:py-44 px-6 md:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-20">
          <div>
            <ScrollReveal>
              <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-sans mb-4">
                Core Divisions
              </p>
              <h2
                data-testid="overview-heading"
                className="font-serif text-3xl md:text-4xl font-bold text-white tracking-tight"
              >
                Institutional Infrastructure
              </h2>
            </ScrollReveal>
          </div>
          <ScrollReveal delay={0.1}>
            <p className="text-sm text-slate-500 max-w-md leading-relaxed">
              Three vertically integrated divisions delivering end-to-end
              sovereign infrastructure for the world's most security-conscious institutions.
            </p>
          </ScrollReveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-slate-800">
          {services.map((service, i) => (
            <ScrollReveal key={service.title} delay={i * 0.1}>
              <div
                data-testid={`service-card-${i}`}
                className={`relative bg-[#0A1325]/50 p-8 lg:p-10 hover:bg-[#0A1325] transition-all duration-500 group h-full flex flex-col ${
                  i < 2 ? "md:border-r border-b md:border-b-0 border-slate-800" : ""
                }`}
              >
                {/* Gold top accent on hover */}
                <div className="absolute top-0 left-0 right-0 h-px bg-[#D4AF37] scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left" />

                <div className="flex items-center justify-between mb-8">
                  <span className="font-serif text-xs text-slate-600 tracking-widest">{service.index}</span>
                  <service.icon className="w-4 h-4 text-[#D4AF37]/60 group-hover:text-[#D4AF37] transition-colors duration-500" strokeWidth={1.5} />
                </div>

                <h3 className="font-serif text-xl lg:text-2xl font-semibold text-white mb-2 tracking-tight">
                  {service.title}
                </h3>
                <p className="text-xs text-[#D4AF37]/70 tracking-wide uppercase mb-5">
                  {service.lead}
                </p>
                <p className="text-sm text-slate-400 leading-relaxed mb-8 flex-1">
                  {service.description}
                </p>

                {/* Metric callout */}
                <div className="border-t border-slate-800 pt-6 mb-6">
                  <span className="font-serif text-2xl font-bold text-white">{service.metric}</span>
                  <span className="block text-[10px] tracking-[0.15em] uppercase text-slate-500 mt-1">{service.metricLabel}</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {service.highlights.map((h) => (
                    <span
                      key={h}
                      className="text-[10px] tracking-wider uppercase text-slate-500 border border-slate-800/80 px-2.5 py-1 group-hover:border-slate-700 transition-colors"
                    >
                      {h}
                    </span>
                  ))}
                </div>

                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] tracking-wide">
                    Learn More <ArrowUpRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
